import type { ReactNode } from 'react';
import { DEFAULT_PANEL_HEADING_LEVEL, type HeadingLevel } from '@/app/panel-heading-levels';

/**
 * 第二層版型：內容面板（INSPINIA 的 `.ibox`）。全站每一塊內容都包在這個殼裡。
 *
 * 原系統對應（`WebContent/css/style.css`）：
 *   `:3661-3665` `.ibox { clear: both; margin-bottom: 25px; margin-top: 0; padding: 0 }`
 *   `:3680-3692` `.ibox-title { background-color: hsla(0,0%,100%,0.90); border-color: #637d4c;
 *                 border-style: solid solid none; border-width: 3px 0 0; color: #666;
 *                 padding: 14px 15px 7px }`  ← 上緣 3px 品牌綠是面板最明顯的識別
 *   `:3695-3703` `.ibox-content { background-color: rgba(255,255,255,0.70);
 *                 padding: 5px 20px 15px 20px; border-color: #e7eaec; border-width: 1px 0px }`
 *   `:3704`      `.ibox-footer`
 *
 * 【證據等級】§7 第 3 級（原始碼）。【雛形值待實機校準】
 */
export interface ContentPanelProps {
  uiId: string;
  /** 面板標題（`.ibox-title` 內的 `<h5>`）。沒有標題時整條 title bar 不畫。 */
  title?: string;
  /**
   * 標題要用第幾級（U4）。預設 `5`，因為舊系統 `.ibox-title` 的標題 89/91 是 `<h5>`
   * （量自 .tmp/ui/raw-dom/legacy 的 143 頁實際 DOM；另見 CrudListPage.tsx 檔頭註解）。
   * 逐頁的例外由 `panelHeadingLevelFor(pageId)` 提供，見 `@/app/panel-heading-levels`。
   * 只影響標籤名，`data-ui-id` 與樣式不變。
   */
  headingLevel?: HeadingLevel;
  /** 標題列右側的操作區（原系統放在 `.ibox-tools`）。 */
  actions?: ReactNode;
  /** `.ibox-footer`。 */
  footer?: ReactNode;
  children: ReactNode;
}

export function ContentPanel({
  uiId,
  title,
  headingLevel = DEFAULT_PANEL_HEADING_LEVEL,
  actions,
  footer,
  children,
}: ContentPanelProps) {
  const Heading = `h${headingLevel}` as const;
  return (
    <section data-ui-id={uiId} className="clear-both mt-0 mb-[25px]">
      {title === undefined && actions === undefined ? null : (
        <div
          data-ui-id={`${uiId}-title`}
          className="flex items-center gap-3 border-brand border-t-[3px] bg-surface/90 px-[15px] pt-[14px] pb-[7px] text-[#666]"
        >
          {title === undefined ? null : (
            <Heading data-ui-id={`${uiId}-heading`} className="flex-1 font-bold text-[18px]">
              {title}
            </Heading>
          )}
          {actions === undefined ? null : (
            <div data-ui-id={`${uiId}-actions`} className="ml-auto flex items-center gap-2">
              {actions}
            </div>
          )}
        </div>
      )}
      <div
        data-ui-id={`${uiId}-content`}
        className="clear-both border-border border-y bg-surface/70 px-5 pt-[5px] pb-[15px]"
      >
        {children}
      </div>
      {footer === undefined ? null : (
        <div
          data-ui-id={`${uiId}-footer`}
          className="border-border border-b bg-surface/70 px-5 py-2 text-sm"
        >
          {footer}
        </div>
      )}
    </section>
  );
}
